import React, { useState, useRef, useEffect } from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { useOnClickOutside } from "../hooks/useOnClickOutside";
import { cn } from "../lib/utils";

interface DropdownOption {
  label: string;
  value: string | number;
}
interface DropdownFilterProps {
  options: DropdownOption[];
  selectedOptions: DropdownOption[];
  onChange: (selected: DropdownOption[]) => void;
  placeholder?: string;
  className?: string;
  triggerClassName?: string;
  value?: string;
  fullWidth?: boolean;
  disabled?: boolean; // ✅ new prop
}

const DropdownFilter: React.FC<DropdownFilterProps> = ({
  options = [],
  selectedOptions = [],
  onChange,
  placeholder = "",
  className,
  triggerClassName,
  value,
  fullWidth = false,
  disabled = false, // ✅ default false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const toggleOption = (option: DropdownOption) => {
    if (disabled) return; // ✅ prevent toggle if disabled

    const isSelected = selectedOptions.some(
      (item) => item.value.toString() === option.value.toString()
    );

    const newSelection = isSelected
      ? selectedOptions.filter(
          (item) => item.value.toString() !== option.value.toString()
        )
      : [...selectedOptions, option];

    onChange(newSelection);
    setIsOpen(false);
  };

  const displayValue =
    value ||
    (selectedOptions.length > 0
      ? selectedOptions.map((opt) => opt.label).join(", ")
      : placeholder);

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle button click to open/close dropdown
  const handleButtonClick = () => {
    if (disabled) return; // ✅ prevent opening if disabled
    setIsOpen(!isOpen);
  };

  // Clear search term when dropdown closes
  useEffect(() => {
    if (!isOpen) setSearchTerm("");
  }, [isOpen]);

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      <button
        type="button"
        ref={buttonRef}
        onClick={handleButtonClick}
        className={cn(
          "flex min-h-8 items-center justify-between px-2.5 py-1.5 text-left border rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary",
          fullWidth ? "w-full" : "w-auto",
          selectedOptions.length > 0 ? "border-primary/50" : "border-input",
          disabled ? "bg-gray-100 cursor-not-allowed" : "bg-white",
          triggerClassName
        )}
        disabled={disabled} // ✅ native disabled attribute
      >
        <span className="truncate">{displayValue}</span>
        <ChevronDown className="w-3.5 h-3.5 ml-2 opacity-70" />
      </button>

      {isOpen && !disabled && (
        <div
          ref={menuRef}
          className={cn(
            "absolute z-[500] bg-white border rounded-md shadow-lg max-h-56 overflow-y-auto mt-1",
            fullWidth ? "w-full" : "min-w-[200px]"
          )}
          style={{
            width: fullWidth && buttonRef.current ? buttonRef.current.offsetWidth : undefined,
          }}
        >
          <div className="p-2 border-b">
            <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-md border">
              <Search className="w-3.5 h-3.5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-full text-xs outline-none bg-transparent"
                autoFocus
                onClick={(e) => e.stopPropagation()}
                disabled={disabled} // ✅ disable input
              />
            </div>
          </div>

          <ul className="py-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <li key={option.value.toString()}>
                  <button
                    type="button"
                    className="flex items-center justify-between w-full px-3 py-1.5 text-xs hover:bg-gray-100 text-start"
                    onClick={() => toggleOption(option)}
                  >
                    {option.label}
                    {selectedOptions.some(
                      (item) => item.value.toString() === option.value.toString()
                    ) && <Check className="w-3.5 h-3.5 text-primary" />}
                  </button>
                </li>
              ))
            ) : (
              <li className="px-3 py-2 text-xs text-gray-500 text-center">
                No results found
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DropdownFilter;

